/**
 * 21/10/2026 — a route test's gateways in the formula's order (user: "how
 * can I see the best in order … the formula works on the total gateway, but
 * route test is different"): #, gateway, sender, score, delivered %, SMS. A
 * gateway that cannot win keeps its row, last, with the reason under it. One
 * table for the Gateways tab and the route test editor's live result.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import { Trophy } from 'lucide-react';
import type { DropGenRouteTestRankRow } from '@trafficpulse/shared';

import { cn } from '@/lib/utils';

const GRID = 'grid grid-cols-[44px_minmax(140px,1.4fr)_minmax(110px,1fr)_72px_84px_64px] items-center gap-3 px-3';
const pct = (v: number | null) => (v === null ? '—' : `${(v * 100).toFixed(v * 100 >= 10 ? 0 : 1)}%`);
const score = (v: number | null) => (v === null ? '—' : v.toFixed(2));

const RankRow: FC<{ row: DropGenRouteTestRankRow; won: boolean }> = ({ row, won }) => (
  <div className={cn('border-t border-border/30 py-1.5 text-xs', won && 'bg-success/5', row.rank === null && 'text-muted-foreground')} data-testid="route-test-rank-row">
    <div className={GRID}>
      <span className="flex items-center gap-1 font-mono font-semibold tabular-nums">
        {won ? <Trophy className="h-3.5 w-3.5 text-success" aria-label="winner" /> : null}
        {row.rank === null ? '—' : `#${row.rank}`}
      </span>
      <span className="truncate font-medium" title={row.routeName ?? undefined}>{row.routeName ?? `Gateway ${row.routeId}`}</span>
      <span className="truncate font-mono text-[11px]" title={row.sender ?? undefined}>{row.sender ?? '—'}</span>
      <span className="text-right font-mono tabular-nums">{score(row.score)}</span>
      <span className="text-right font-mono tabular-nums">{pct(row.deliveredRate)}</span>
      <span className="text-right font-mono tabular-nums">{row.smsSent.toLocaleString('en-US')}</span>
    </div>
    {row.reason ? <p className="px-3 pl-[68px] pt-0.5 text-[11px] text-muted-foreground" data-testid="route-test-rank-reason">{row.reason}</p> : null}
  </div>
);

export const RankingTable: FC<{ rows: DropGenRouteTestRankRow[]; won?: boolean; className?: string }> = ({ rows, won = false, className }) => (
  <div className={cn('overflow-x-auto', className)} data-testid="route-test-ranking">
    <div className="min-w-[560px]">
      <div className={`${GRID} py-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground`}>
        <span>#</span>
        <span>Gateway</span>
        <span>Sender</span>
        <span className="text-right">Score</span>
        <span className="text-right">Delivered</span>
        <span className="text-right">SMS</span>
      </div>
      {rows.map((r) => (
        <RankRow key={r.routeId} row={r} won={won && r.rank === 1} />
      ))}
    </div>
  </div>
);
